import { forwardRef, type SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  options: SelectOption[];
  placeholder?: string;
  size?: "sm" | "md";
}

/**
 * A native `<select>`, on purpose.
 *
 * Radix's Select is in the dependency list and looks better, but a filter bar is
 * keyboard-first: a native select types-to-select, works on touch, and is one
 * tab stop. The custom one is worth it only where an option needs rich content,
 * which nothing in phase 1 does.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, options, placeholder, size = "md", ...props }, ref) => (
    <div className="relative inline-flex">
      <select
        ref={ref}
        className={cn(
          "appearance-none w-full bg-[rgba(255,255,255,0.03)] border border-[var(--color-border)] rounded-md",
          "text-[var(--color-fg)] transition-colors cursor-pointer",
          "focus:outline-none focus:border-[var(--color-accent)] focus:bg-[rgba(255,255,255,0.05)]",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          size === "sm" ? "pl-2 pr-7 py-1 text-xs" : "pl-3 pr-8 py-1.5 text-[13px]",
          className,
        )}
        {...props}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={size === "sm" ? 12 : 14}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-fg-subtle)]"
      />
    </div>
  ),
);
Select.displayName = "Select";
